import { useEffect, useState } from 'react';
import '../styles/SamvadMatchingCard.css';

interface ActiveMatch {
  match_id: string;
  partner_id: string;
  partner_display: string;
  since: string;
}

interface IncomingRequest {
  id: string;
  requester_id: string;
  requester_display: string;
  requested_at: string;
}

interface Candidate {
  participant_id: string;
  display_name: string;
  proficiency: number;
  pincode_match: boolean;
  score: number;
}

interface Props {
  participantId: string;
  onNavigate: (path: string) => void;
}

// ── ICS download helper ───────────────────────────────────────────────────────
function downloadICS(matchId: string, partnerDisplay: string) {
  const now   = new Date();
  const start = new Date(now.getTime() + 24 * 60 * 60 * 1000); // tomorrow same time
  const fmt   = (d: Date) =>
    d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Raajadharma//Samvad//EN',
    'BEGIN:VEVENT',
    `UID:${matchId}@raajadharma.org`,
    `DTSTAMP:${fmt(now)}Z`,
    `DTSTART:${fmt(start)}Z`,
    'DURATION:PT45M',
    `SUMMARY:Sanskrit Samvad — ${partnerDisplay}`,
    'DESCRIPTION:Practice Sanskrit conversation with your matched partner.\\nLog the session at https://raajadharma.org/samvad/session-log',
    'URL:https://raajadharma.org/samvad/session-log',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `samvad-${matchId}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

interface LookedUpParticipant {
  participant_id: string;
  display_name:   string;
  proficiency:    number;
  archetype:      string;
}

const PROF_LABEL = (n: number) =>
  ['', '१ - Beginner', '२ - Basic', '३ - Intermediate', '४ - Advanced', '५ - Fluent'][n] ?? `${n}`;

// ── Main component ────────────────────────────────────────────────────────────
export default function SamvadMatchingCard({ participantId, onNavigate }: Props) {
  const [activeMatches,  setActiveMatches]  = useState<ActiveMatch[]>([]);
  const [incomingReqs,   setIncomingReqs]   = useState<IncomingRequest[]>([]);
  const [candidates,     setCandidates]     = useState<Candidate[]>([]);
  const [loadingActive,  setLoadingActive]  = useState(true);
  const [loadingIncoming, setLoadingIncoming] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [requestSent,    setRequestSent]    = useState<string | null>(null);
  const [respondResult,  setRespondResult]  = useState<string | null>(null);
  const [error,          setError]          = useState<string | null>(null);

  // Direct lookup state
  const [lookupInput,    setLookupInput]    = useState('');
  const [lookupResult,   setLookupResult]   = useState<LookedUpParticipant | null>(null);
  const [lookupError,    setLookupError]    = useState<string | null>(null);
  const [lookupLoading,  setLookupLoading]  = useState(false);

  const uid = participantId;

  // Load active matches
  useEffect(() => {
    if (!uid) { setLoadingActive(false); return; }
    fetch(`/api/samvad/match/active/${uid}`)
      .then(r => r.json())
      .then(d => { setActiveMatches(d.active_matches ?? []); setLoadingActive(false); })
      .catch(() => setLoadingActive(false));
  }, [uid, respondResult, requestSent]);

  // Load incoming requests
  useEffect(() => {
    if (!uid) { setLoadingIncoming(false); return; }
    fetch(`/api/samvad/match/requests/${uid}`)
      .then(r => r.json())
      .then(d => { setIncomingReqs(d.requests ?? []); setLoadingIncoming(false); })
      .catch(() => setLoadingIncoming(false));
  }, [uid, respondResult]);

  // Find partner candidates — called automatically and on manual refresh
  function findPartners() {
    if (!uid) return;
    setLoadingCandidates(true);
    setError(null);
    fetch(`/api/samvad/match/${uid}`)
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error((d as { error?: string; detail?: string }).error ?? (d as { detail?: string }).detail ?? `Error ${r.status}`); });
        return r.json();
      })
      .then(d => { setCandidates((d as { candidates?: Candidate[] }).candidates ?? []); })
      .catch((e: Error) => setError(e.message || 'सर्वर से संपर्क नहीं हो सका।'))
      .finally(() => setLoadingCandidates(false));
  }

  // Auto-load candidates when uid is first available
  useEffect(() => {
    if (uid) findPartners();
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Direct lookup by participant ID
  async function lookupParticipant() {
    const pid = lookupInput.trim().toUpperCase();
    if (!pid) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const params = uid ? `?requester_id=${encodeURIComponent(uid)}` : '';
      const res = await fetch(`/api/samvad/participant/${encodeURIComponent(pid)}${params}`);
      const data = await res.json() as LookedUpParticipant & { error?: string; detail?: string };
      if (!res.ok) {
        setLookupError(data.error ?? data.detail ?? 'प्रतिभागी नहीं मिला।');
      } else if (data.participant_id === uid) {
        setLookupError('यह आपका अपना ID है।');
      } else {
        setLookupResult(data);
      }
    } catch {
      setLookupError('सर्वर से संपर्क नहीं हो सका।');
    } finally {
      setLookupLoading(false);
    }
  }

  // Send match request (also clears direct lookup result on success)
  async function sendRequest(targetId: string) {
    setError(null);
    try {
      const res = await fetch('/api/samvad/match/request', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ requester_id: uid, target_id: targetId }),
      });
      const data = await res.json() as { detail?: string; error?: string };
      if (!res.ok) { setError(data.error ?? data.detail ?? 'अनुरोध भेजने में त्रुटि'); return; }
      setRequestSent(targetId);
      setCandidates(prev => prev.filter(c => c.participant_id !== targetId));
      if (lookupResult?.participant_id === targetId) setLookupResult(null);
    } catch {
      setError('सर्वर से संपर्क नहीं हो सका।');
    }
  }

  // Respond to incoming request
  async function respond(requestId: string, action: 'accept' | 'decline') {
    setError(null);
    try {
      const res = await fetch('/api/samvad/match/respond', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ uid, request_id: requestId, action }),
      });
      const data = await res.json() as { detail?: string; error?: string };
      if (!res.ok) { setError(data.error ?? data.detail ?? 'प्रतिक्रिया में त्रुटि'); return; }
      setRespondResult(`${requestId}-${action}`);
      setIncomingReqs(prev => prev.filter(r => r.id !== requestId));
    } catch {
      setError('सर्वर से संपर्क नहीं हो सका।');
    }
  }

  const profLabel = (n: number) => PROF_LABEL(n);

  return (
    <div className="svmc-card">
      <h3 className="svmc-title">🤝 भागीदार युगलन · Partner Matching</h3>

      {error && <div className="svmc-error">{error}</div>}

      {/* ── Direct lookup by participant ID ── */}
      <section className="svmc-section svmc-section--lookup">
        <h4 className="svmc-section-heading">🔎 ID से साझेदार खोजें · Look Up by Participant ID</h4>
        <p className="svmc-find-hint">
          साझेदार का ID दर्ज करें (जैसे <code>SV-2025-00003</code>) और सीधे अनुरोध भेजें।
          <br /><span className="svmc-muted">Enter your partner's participant code to send a direct request.</span>
        </p>
        <div className="svmc-lookup-row">
          <input
            className="svmc-lookup-input"
            type="text"
            placeholder="SV-2025-00001"
            value={lookupInput}
            onChange={e => { setLookupInput(e.target.value); setLookupResult(null); setLookupError(null); }}
            onKeyDown={e => e.key === 'Enter' && lookupParticipant()}
          />
          <button
            className="svmc-btn svmc-btn--primary"
            onClick={lookupParticipant}
            disabled={lookupLoading || !lookupInput.trim()}
          >
            {lookupLoading ? '…' : '🔎 खोजें'}
          </button>
        </div>
        {lookupError && <div className="svmc-lookup-error">⚠ {lookupError}</div>}
        {lookupResult && (
          <div className="svmc-lookup-result">
            <span className="svmc-partner-name">👤 {lookupResult.display_name}</span>
            <span className="svmc-partner-id">{lookupResult.participant_id}</span>
            <span className="svmc-badge">{profLabel(lookupResult.proficiency)}</span>
            <button
              className="svmc-btn svmc-btn--primary svmc-btn--sm"
              onClick={() => sendRequest(lookupResult.participant_id)}
              disabled={requestSent === lookupResult.participant_id}
            >
              {requestSent === lookupResult.participant_id ? '✅ अनुरोध भेजा' : '📨 अनुरोध भेजें'}
            </button>
          </div>
        )}
        {requestSent && lookupResult === null && (
          <div className="svmc-success">✅ अनुरोध भेज दिया गया!</div>
        )}
      </section>

      {/* ── Active matches ── */}
      <section className="svmc-section">
        <h4 className="svmc-section-heading">✅ सक्रिय साझेदार · Active Partners</h4>
        {loadingActive ? (
          <p className="svmc-loading">लोड हो रहा है…</p>
        ) : activeMatches.length === 0 ? (
          <p className="svmc-empty">अभी कोई सक्रिय साझेदार नहीं। ऊपर ID दर्ज करें या नीचे खोज करें।</p>
        ) : (
          <ul className="svmc-list">
            {activeMatches.map(m => (
              <li key={m.match_id} className="svmc-match-row">
                <span className="svmc-partner-name">👤 {m.partner_display}</span>
                <span className="svmc-partner-id">{m.partner_id}</span>
                <div className="svmc-match-actions">
                  <button
                    className="svmc-btn svmc-btn--sm"
                    onClick={() => downloadICS(m.match_id, m.partner_display)}
                    title="Add practice session to calendar"
                  >
                    📅 Calendar
                  </button>
                  <button
                    className="svmc-btn svmc-btn--primary svmc-btn--sm"
                    onClick={() => onNavigate('/samvad/session-log')}
                  >
                    📝 Log Session
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Incoming requests ── */}
      {(loadingIncoming || incomingReqs.length > 0) && (
        <section className="svmc-section">
          <h4 className="svmc-section-heading">🔔 आने वाले अनुरोध · Incoming Requests</h4>
          {loadingIncoming ? (
            <p className="svmc-loading">लोड हो रहा है…</p>
          ) : (
            <ul className="svmc-list">
              {incomingReqs.map(req => (
                <li key={req.id} className="svmc-req-row">
                  <span className="svmc-partner-name">👤 {req.requester_display}</span>
                  <span className="svmc-partner-id">{req.requester_id}</span>
                  <div className="svmc-match-actions">
                    <button
                      className="svmc-btn svmc-btn--accept svmc-btn--sm"
                      onClick={() => respond(req.id, 'accept')}
                    >
                      ✅ स्वीकार
                    </button>
                    <button
                      className="svmc-btn svmc-btn--decline svmc-btn--sm"
                      onClick={() => respond(req.id, 'decline')}
                    >
                      ✗ अस्वीकार
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── Algorithmic discovery ── */}
      <section className="svmc-section">
        <div className="svmc-section-header">
          <h4 className="svmc-section-heading">🔍 अनुशंसित साझेदार · Suggested Partners</h4>
          <button
            className="svmc-btn svmc-btn--secondary svmc-btn--sm"
            onClick={findPartners}
            disabled={loadingCandidates}
            title="Refresh suggestions"
          >
            {loadingCandidates ? '🔄' : '↻ ताज़ा करें'}
          </button>
        </div>
        <p className="svmc-find-hint">
          पिनकोड, स्तर और Archetype के आधार पर साझेदार सुझाए जाते हैं।
          <br /><span className="svmc-muted">Matched by pincode → proficiency → archetype.</span>
        </p>
        {loadingCandidates ? (
          <p className="svmc-loading">🔄 साझेदार खोज रहे हैं…</p>
        ) : candidates.length === 0 ? (
          <p className="svmc-muted">
            अभी कोई सुझाव नहीं — अधिक प्रतिभागी पंजीकृत होने पर दिखेंगे।
            <br /><em>No suggestions yet — check back once more participants register.</em>
          </p>
        ) : (
          <>
            {requestSent && (
              <div className="svmc-success">
                ✅ अनुरोध भेज दिया गया! जब साझेदार स्वीकार करे, वह Active Partners में दिखेगा।
              </div>
            )}
            <ul className="svmc-list">
              {candidates.map(c => (
                <li key={c.participant_id} className="svmc-candidate-row">
                  <div className="svmc-candidate-info">
                    <span className="svmc-partner-name">👤 {c.display_name}</span>
                    <div className="svmc-candidate-meta">
                      <span className="svmc-badge">{profLabel(c.proficiency)}</span>
                      {c.pincode_match && <span className="svmc-badge svmc-badge--local">📍 Same area</span>}
                    </div>
                  </div>
                  <button
                    className="svmc-btn svmc-btn--primary svmc-btn--sm"
                    onClick={() => sendRequest(c.participant_id)}
                    disabled={requestSent === c.participant_id}
                  >
                    {requestSent === c.participant_id ? '✅ भेजा' : '📨 अनुरोध भेजें'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
